/**
 * QMS Ticket Wizard
 * 3-step wizard for creating new tickets
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X, ChevronLeft, ChevronRight, Save, Send, RefreshCw, AlertTriangle } from 'lucide-react';
import { useSession } from '@/hooks/useSession';
import { useCreateQmsTicket, useLinkQmsTicket } from '../../hooks/useQmsTicket';
import { useUploadQmsAttachment } from '../../hooks/useQmsAttachments';
import { usePendingFiles } from '../../hooks/usePendingFiles';
import { QmsStepper, type WizardStep } from './QmsStepper';
import { QmsRichTextEditor } from '../shared/QmsRichTextEditor';
import { QmsPendingAttachments } from '../shared/QmsPendingAttachments';
import { QmsTicketLinkSelector, type PendingLink } from './QmsTicketLinkSelector';
import {
  QmsDepartmentSelect,
  QmsPrioritySelect,
  QmsSeveritySelect,
  QmsSubtypeSelect,
} from '../shared/QmsFormFields';
import { QmsAccountSelect } from '../shared/QmsAccountSelect';
import { getQmsOrganisationId } from '../../utils/organisation';
import { parseQmsTags } from '../../utils/tags';
import type {
  QmsTicket,
  QmsTicketPayload,
  QmsDepartment,
  QmsPriority,
  QmsSeverityLevel,
  QmsSubtype,
} from '../../types/qms.types';

interface QmsTicketWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onTicketCreated: (ticket: QmsTicket) => void;
}

interface FormData {
  // Step 1: Details
  title: string;
  subtype: QmsSubtype | '';
  department: QmsDepartment | '';
  description: string;
  priority: QmsPriority;
  severity_level: QmsSeverityLevel;
  tags: string[];
  account_id?: string;
  pending_links: PendingLink[];

  // Step 2: Investigation
  root_cause_analysis: string;
  investigation_findings: string;
  assigned_to: string;
  due_date: string;

  // Step 3: CAPA Decision
  needs_capa: boolean;
}

const initialFormData: FormData = {
  title: '',
  subtype: '',
  department: '',
  description: '',
  priority: 3,
  severity_level: 1,
  tags: [],
  account_id: undefined,
  pending_links: [],
  root_cause_analysis: '',
  investigation_findings: '',
  assigned_to: '',
  due_date: '',
  needs_capa: false,
};

export function QmsTicketWizard({
  isOpen,
  onClose,
  onTicketCreated,
}: QmsTicketWizardProps) {
  const { t } = useTranslation('qms');
  const { user } = useSession();
  const createMutation = useCreateQmsTicket();
  const uploadAttachment = useUploadQmsAttachment();
  const linkMutation = useLinkQmsTicket();
  const {
    pendingFiles,
    addFiles,
    removeFile,
    clearFiles,
    updateFileStatus,
    hasPendingFiles,
  } = usePendingFiles();

  const [currentStep, setCurrentStep] = useState(0);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isLinkingTickets, setIsLinkingTickets] = useState(false);
  const [linkingProgress, setLinkingProgress] = useState({ current: 0, total: 0 });
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [tagsInput, setTagsInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Track if ticket was created but has failed uploads (allows retry)
  const [createdTicket, setCreatedTicket] = useState<QmsTicket | null>(null);
  const [hasFailedUploads, setHasFailedUploads] = useState(false);

  const steps: WizardStep[] = [
    { id: 'details', title: t('wizard.steps.details') },
    { id: 'investigation', title: t('wizard.steps.investigation') },
    { id: 'capa-decision', title: t('wizard.steps.capaDecision') },
  ];

  // Validation per step
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 0) {
      if (!formData.title.trim()) {
        newErrors.title = t('validation.titleRequired');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepClick = (stepIndex: number) => {
    // Only allow going back to completed steps or current step
    if (stepIndex <= currentStep || completedSteps.includes(stepIndex)) {
      setCurrentStep(stepIndex);
    }
  };

  const buildPayload = (): QmsTicketPayload => {
    const tags = parseQmsTags(tagsInput);

    return {
      type: 'ticket',
      title: formData.title,
      subtype: formData.subtype || undefined,
      department: formData.department || undefined,
      description: formData.description || undefined,
      priority: formData.priority,
      severity_level: formData.severity_level,
      tags: tags.length > 0 ? tags : undefined,
      account_id: formData.account_id || undefined,
      root_cause_analysis: formData.root_cause_analysis || undefined,
      investigation_findings: formData.investigation_findings || undefined,
      assigned_to: formData.assigned_to || undefined,
      due_date: formData.due_date || undefined,
      needs_capa: formData.needs_capa,
      status: 'submitted',
      organisation: getQmsOrganisationId(user?.organisation),
    };
  };

  // Upload pending files after ticket creation
  const uploadPendingFiles = async (ticketId: string): Promise<{ success: number; failed: number }> => {
    if (!hasPendingFiles) return { success: 0, failed: 0 };

    setIsUploadingFiles(true);
    let successCount = 0;
    let failedCount = 0;

    for (const pendingFile of pendingFiles) {
      updateFileStatus(pendingFile.id, 'uploading');
      try {
        await uploadAttachment.mutateAsync({ ticketId, file: pendingFile.file });
        updateFileStatus(pendingFile.id, 'success');
        successCount++;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        updateFileStatus(pendingFile.id, 'error', errorMessage);
        failedCount++;
      }
    }

    setIsUploadingFiles(false);
    return { success: successCount, failed: failedCount };
  };

  // Show toast based on upload results
  const showUploadToast = (success: number, failed: number) => {
    if (success > 0 && failed === 0) {
      toast.success(t('attachments.pending.uploadSuccess', { count: success }));
    } else if (failed > 0 && success > 0) {
      toast.warning(t('attachments.pending.uploadPartial', { success, failed }));
    } else if (failed > 0 && success === 0) {
      toast.error(t('attachments.pending.uploadFailed'));
    }
  };

  // Link pending tickets after ticket creation
  const linkPendingTickets = async (ticketId: string, links: PendingLink[]): Promise<{ success: number; failed: number }> => {
    if (links.length === 0) return { success: 0, failed: 0 };

    setIsLinkingTickets(true);
    setLinkingProgress({ current: 0, total: links.length });
    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < links.length; i++) {
      const link = links[i];
      setLinkingProgress({ current: i + 1, total: links.length });
      try {
        await linkMutation.mutateAsync({
          ticketId,
          relatedTicketId: link.ticket_id,
          relationship: link.relationship,
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to link ticket ${link.ticket_id}:`, error);
        failedCount++;
      }
    }

    setIsLinkingTickets(false);
    return { success: successCount, failed: failedCount };
  };

  // Show toast based on linking results
  const showLinkToast = (success: number, failed: number) => {
    if (success > 0 && failed === 0) {
      toast.success(t('wizard.linkSuccess', { count: success, defaultValue: '{{count}} ticket(s) linked successfully' }));
    } else if (failed > 0 && success > 0) {
      toast.warning(t('wizard.linkPartial', { success, failed, defaultValue: '{{success}} linked, {{failed}} failed' }));
    } else if (failed > 0 && success === 0) {
      toast.error(t('wizard.linkFailed', 'Failed to link tickets'));
    }
  };

  const handleSaveDraft = async () => {
    if (!validateStep(0)) return; // At minimum, title is required
    setSubmitError(null);
    setHasFailedUploads(false);

    try {
      const payload = buildPayload();
      payload.status = 'submitted'; // Draft tickets start as submitted
      const ticket = await createMutation.mutateAsync(payload);
      setCreatedTicket(ticket);

      // Upload pending files if any
      if (hasPendingFiles) {
        const { success, failed } = await uploadPendingFiles(ticket._id);
        showUploadToast(success, failed);

        // If any uploads failed, keep wizard open for retry
        if (failed > 0) {
          setHasFailedUploads(true);
          onTicketCreated(ticket); // Still notify parent of ticket creation
          return; // Don't close - allow user to retry or close manually
        }
      }

      // Link pending tickets if any (capture current links before async operations)
      const linksToCreate = [...formData.pending_links];
      if (linksToCreate.length > 0) {
        const { success, failed } = await linkPendingTickets(ticket._id, linksToCreate);
        showLinkToast(success, failed);
        // Note: We don't block on link failures - they're non-critical
      }

      onTicketCreated(ticket);
      handleClose();
    } catch (error) {
      console.error('Failed to save draft:', error);
      setSubmitError(t('errors.savingTicket'));
    }
  };

  const handleSubmit = async () => {
    // Validate all steps
    for (let i = 0; i <= currentStep; i++) {
      if (!validateStep(i)) {
        setCurrentStep(i);
        return;
      }
    }
    setSubmitError(null);
    setHasFailedUploads(false);

    try {
      const payload = buildPayload();
      const ticket = await createMutation.mutateAsync(payload);
      setCreatedTicket(ticket);

      // Upload pending files if any
      if (hasPendingFiles) {
        const { success, failed } = await uploadPendingFiles(ticket._id);
        showUploadToast(success, failed);

        // If any uploads failed, keep wizard open for retry
        if (failed > 0) {
          setHasFailedUploads(true);
          onTicketCreated(ticket); // Still notify parent of ticket creation
          return; // Don't close - allow user to retry or close manually
        }
      }

      // Link pending tickets if any (capture current links before async operations)
      const linksToCreate = [...formData.pending_links];
      if (linksToCreate.length > 0) {
        const { success, failed } = await linkPendingTickets(ticket._id, linksToCreate);
        showLinkToast(success, failed);
        // Note: We don't block on link failures - they're non-critical
      }

      onTicketCreated(ticket);
      handleClose();
    } catch (error) {
      console.error('Failed to create ticket:', error);
      setSubmitError(t('errors.savingTicket'));
    }
  };

  // Retry failed uploads after ticket creation
  const handleRetryUploads = async () => {
    if (!createdTicket) return;
    setHasFailedUploads(false);

    // Filter only files that failed
    const failedFiles = pendingFiles.filter(f => f.status === 'error');
    if (failedFiles.length === 0) {
      handleClose();
      return;
    }

    setIsUploadingFiles(true);
    let successCount = 0;
    let failedCount = 0;

    for (const pendingFile of failedFiles) {
      updateFileStatus(pendingFile.id, 'uploading');
      try {
        await uploadAttachment.mutateAsync({ ticketId: createdTicket._id, file: pendingFile.file });
        updateFileStatus(pendingFile.id, 'success');
        successCount++;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        updateFileStatus(pendingFile.id, 'error', errorMessage);
        failedCount++;
      }
    }

    setIsUploadingFiles(false);
    showUploadToast(successCount, failedCount);

    if (failedCount > 0) {
      setHasFailedUploads(true);
    } else {
      handleClose();
    }
  };

  // Close wizard even if uploads failed (user acknowledges)
  const handleCloseAnyway = () => {
    handleClose();
  };

  const handleClose = () => {
    setCurrentStep(0);
    setFormData(initialFormData);
    setTagsInput('');
    setErrors({});
    setCompletedSteps([]);
    setSubmitError(null);
    setCreatedTicket(null);
    setHasFailedUploads(false);
    clearFiles(); // Clear pending files
    setIsUploadingFiles(false);
    setIsLinkingTickets(false);
    setLinkingProgress({ current: 0, total: 0 });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
        <div className="w-screen max-w-2xl transform transition-transform">
          <div className="flex h-full flex-col bg-white shadow-xl">
            {/* Header */}
            <div className="bg-atek-blue px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">
                  {t('wizard.createTicket')}
                </h2>
                <button
                  onClick={handleClose}
                  className="rounded-lg p-2 text-white hover:bg-atek-blue-dark transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Stepper */}
            <div className="px-6 py-4 border-b bg-gray-50">
              <QmsStepper
                steps={steps}
                currentStep={currentStep}
                completedSteps={completedSteps}
                onStepClick={handleStepClick}
              />
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Step 1: Details */}
              {currentStep === 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('wizard.steps.details')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('wizard.detailsDescription')}
                  </p>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.fields.title')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-atek-blue focus:border-transparent ${
                        errors.title ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('form.placeholders.title')}
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                    )}
                  </div>

                  {/* Subtype & Department */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('form.fields.subtype')}
                      </label>
                      <QmsSubtypeSelect
                        value={formData.subtype}
                        onChange={(value) =>
                          setFormData({ ...formData, subtype: value })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('form.fields.department')}
                      </label>
                      <QmsDepartmentSelect
                        value={formData.department}
                        onChange={(value) =>
                          setFormData({ ...formData, department: value })
                        }
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.fields.description')}
                    </label>
                    <QmsRichTextEditor
                      value={formData.description}
                      onChange={(html) =>
                        setFormData({ ...formData, description: html })
                      }
                      placeholder={t('form.placeholders.description')}
                    />
                  </div>

                  {/* Priority & Severity */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('form.fields.priority')}
                      </label>
                      <QmsPrioritySelect
                        value={formData.priority}
                        onChange={(value) =>
                          setFormData({ ...formData, priority: value })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('form.fields.severityLevel')}
                      </label>
                      <QmsSeveritySelect
                        value={formData.severity_level}
                        onChange={(value) =>
                          setFormData({ ...formData, severity_level: value })
                        }
                      />
                    </div>
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.fields.tags')}
                    </label>
                    <input
                      type="text"
                      value={tagsInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTagsInput(value);
                        setFormData({
                          ...formData,
                          tags: parseQmsTags(value),
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                      placeholder={t('form.placeholders.tags')}
                    />
                  </div>

                  {/* Account */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.fields.account')}
                    </label>
                    <QmsAccountSelect
                      value={formData.account_id}
                      onChange={(accountId) =>
                        setFormData({ ...formData, account_id: accountId })
                      }
                    />
                  </div>

                  {/* Link to Existing Tickets */}
                  <div className="border-t pt-4 mt-4">
                    <QmsTicketLinkSelector
                      selectedLinks={formData.pending_links}
                      onLinksChange={(links) =>
                        setFormData({ ...formData, pending_links: links })
                      }
                      disabled={createMutation.isPending || isUploadingFiles || isLinkingTickets}
                    />
                  </div>

                  {/* Attachments */}
                  <div className="border-t pt-4 mt-4">
                    <QmsPendingAttachments
                      pendingFiles={pendingFiles}
                      onAddFiles={addFiles}
                      onRemoveFile={removeFile}
                      onClearAll={clearFiles}
                      disabled={createMutation.isPending}
                      isUploading={isUploadingFiles}
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Investigation */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('wizard.steps.investigation')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('wizard.investigationDescription')}
                  </p>

                  {/* Root Cause Analysis */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.fields.rootCause')}
                    </label>
                    <QmsRichTextEditor
                      value={formData.root_cause_analysis}
                      onChange={(html) =>
                        setFormData({ ...formData, root_cause_analysis: html })
                      }
                      placeholder={t('form.placeholders.rootCause')}
                    />
                  </div>

                  {/* Investigation Findings */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.fields.investigationFindings')}
                    </label>
                    <QmsRichTextEditor
                      value={formData.investigation_findings}
                      onChange={(html) =>
                        setFormData({ ...formData, investigation_findings: html })
                      }
                      placeholder={t('form.placeholders.investigationFindings')}
                    />
                  </div>

                  {/* Due Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('form.fields.dueDate')}
                    </label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) =>
                        setFormData({ ...formData, due_date: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-atek-blue"
                    />
                  </div>
                </div>
              )}

              {/* Step 3: CAPA Decision */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('wizard.steps.capaDecision')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('wizard.capaDecisionDescription')}
                  </p>

                  {/* CAPA Decision */}
                  <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                    <div className="flex items-start gap-4">
                      <input
                        type="checkbox"
                        id="needs_capa"
                        checked={formData.needs_capa}
                        onChange={(e) =>
                          setFormData({ ...formData, needs_capa: e.target.checked })
                        }
                        className="mt-1 h-5 w-5 rounded border-gray-300 text-atek-blue focus:ring-atek-blue"
                      />
                      <div>
                        <label
                          htmlFor="needs_capa"
                          className="text-base font-medium text-gray-900"
                        >
                          {t('wizard.needsCapa')}
                        </label>
                        <p className="text-sm text-gray-600 mt-1">
                          {t('wizard.needsCapaDescription')}
                        </p>
                      </div>
                    </div>

                    {formData.needs_capa && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-800">
                          {t('wizard.capaWillBeCreated')}
                        </p>
                      </div>
                    )}

                    {!formData.needs_capa && (
                      <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                        <p className="text-sm text-gray-600">
                          {t('wizard.noCapaNeeded')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Summary */}
                  <div className="border-t pt-4 mt-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                      {t('wizard.summary')}
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">{t('form.fields.title')}:</dt>
                        <dd className="text-gray-900 font-medium">{formData.title}</dd>
                      </div>
                      {formData.department && (
                        <div className="flex justify-between">
                          <dt className="text-gray-500">{t('form.fields.department')}:</dt>
                          <dd className="text-gray-900">{t(`departmentsFull.${formData.department}`)}</dd>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <dt className="text-gray-500">{t('form.fields.priority')}:</dt>
                        <dd className="text-gray-900">{t(`priority.${formData.priority}`)}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">{t('form.fields.severityLevel')}:</dt>
                        <dd className="text-gray-900">{t(`severity.level${formData.severity_level}`)}</dd>
                      </div>
                      {hasPendingFiles && (
                        <div className="flex justify-between">
                          <dt className="text-gray-500">{t('attachments.pending.title')}:</dt>
                          <dd className="text-gray-900">
                            {t('attachments.pending.count', { count: pendingFiles.length })}
                          </dd>
                        </div>
                      )}
                      {formData.pending_links.length > 0 && (
                        <div className="flex justify-between">
                          <dt className="text-gray-500">{t('wizard.linkedTickets', 'Linked Tickets')}:</dt>
                          <dd className="text-gray-900">
                            {t('wizard.linkedTicketsCount', { count: formData.pending_links.length, defaultValue: '{{count}} ticket(s)' })}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t px-6 py-4 bg-gray-50">
              {/* Error display */}
              {submitError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{submitError}</p>
                </div>
              )}

              {/* Failed uploads warning - show when ticket created but uploads failed */}
              {hasFailedUploads && createdTicket && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        {t('attachments.pending.uploadFailedWarning', 'Ticket created but some files failed to upload')}
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        {t('attachments.pending.retryOrClose', 'You can retry the failed uploads or close and upload them later from the ticket detail page.')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  {currentStep > 0 && !hasFailedUploads && (
                    <button
                      type="button"
                      onClick={handleBack}
                      disabled={createMutation.isPending || isUploadingFiles || isLinkingTickets}
                      className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {t('wizard.back')}
                    </button>
                  )}
                </div>

                {/* Show retry/close buttons when uploads failed */}
                {hasFailedUploads && createdTicket ? (
                  <div className="flex items-center gap-3 ml-auto">
                    <button
                      type="button"
                      onClick={handleCloseAnyway}
                      disabled={isUploadingFiles}
                      className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                      {t('wizard.closeAnyway', 'Close Anyway')}
                    </button>
                    <button
                      type="button"
                      onClick={handleRetryUploads}
                      disabled={isUploadingFiles}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
                    >
                      <RefreshCw className={`h-4 w-4 ${isUploadingFiles ? 'animate-spin' : ''}`} />
                      {isUploadingFiles
                        ? t('attachments.pending.retrying', 'Retrying...')
                        : t('attachments.pending.retryFailed', 'Retry Failed Uploads')}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={createMutation.isPending || isUploadingFiles || isLinkingTickets}
                      className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                      <Save className={`h-4 w-4 ${(createMutation.isPending || isUploadingFiles || isLinkingTickets) ? 'animate-pulse' : ''}`} />
                      {createMutation.isPending
                        ? t('wizard.creating')
                        : isUploadingFiles
                        ? t('wizard.uploading', 'Uploading...')
                        : isLinkingTickets
                        ? t('wizard.linking', { current: linkingProgress.current, total: linkingProgress.total, defaultValue: 'Linking ({{current}}/{{total}})...' })
                        : t('wizard.saveDraft')}
                    </button>

                    {currentStep < steps.length - 1 ? (
                      <button
                        type="button"
                        onClick={handleNext}
                        disabled={createMutation.isPending || isUploadingFiles || isLinkingTickets}
                        className="flex items-center gap-2 px-4 py-2 bg-atek-blue text-white rounded-lg hover:bg-atek-blue-dark transition-colors disabled:opacity-50"
                      >
                        {t('wizard.next')}
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={createMutation.isPending || isUploadingFiles || isLinkingTickets}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        <Send className="h-4 w-4" />
                        {createMutation.isPending
                          ? t('wizard.creating')
                          : isUploadingFiles
                          ? t('wizard.uploading', 'Uploading...')
                          : isLinkingTickets
                          ? t('wizard.linking', { current: linkingProgress.current, total: linkingProgress.total, defaultValue: 'Linking ({{current}}/{{total}})...' })
                          : t('wizard.createAndSubmit')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
